import axios from 'axios';
import { API_BASE_URL } from '../config/api.js';
import { parseMoneyAmountSR } from '../utils/displayUtils';

// Create axios instance with auth interceptor for dashboard actions
const API = axios.create({ baseURL: `${API_BASE_URL}/api` });

// Interceptor to add auth token
API.interceptors.request.use((req) => {
  const profile = localStorage.getItem('profile');
  if (profile) {
    try {
      const parsedProfile = JSON.parse(profile);
      if (parsedProfile.token) {
        req.headers.Authorization = `Bearer ${parsedProfile.token}`;
      }
    } catch (e) {
      // Silent fail
    }
  }
  return req;
});

// Dashboard Action Types
export const FETCH_ACTIVE_JOBS_COUNT = 'FETCH_ACTIVE_JOBS_COUNT';
export const FETCH_ACTIVE_CLIENTS_COUNT = 'FETCH_ACTIVE_CLIENTS_COUNT';
export const FETCH_TOTAL_REVENUE = 'FETCH_TOTAL_REVENUE';
export const FETCH_WEBSHOP_ORDERS_COUNT = 'FETCH_WEBSHOP_ORDERS_COUNT';
export const FETCH_CALENDAR_JOBS = 'FETCH_CALENDAR_JOBS';
export const RESET_DASHBOARD = 'RESET_DASHBOARD';

export const resetDashboard = () => ({ type: RESET_DASHBOARD });

// Helper function to get date range based on period
const getDateRangeForPeriod = (period) => {
  const now = new Date();
  let startDate, endDate, prevStartDate, prevEndDate;
  
  switch(period) {
    case 'day':
      // Today (current day)
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      
      // Yesterday (previous day for comparison)
      prevEndDate = new Date(now);
      prevEndDate.setDate(now.getDate() - 1);
      prevEndDate.setHours(23, 59, 59, 999);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
    
    case 'week': {
      // Current week (from Monday to today)
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday to 6
      
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now);
      startDate.setDate(now.getDate() - daysFromMonday);
      startDate.setHours(0, 0, 0, 0);
      
      // Previous week (for comparison - same length as current week so far)
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevEndDate.getDate() - daysFromMonday);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
    }
    
    case 'month':
      // Current month (from 1st of this month to today)
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      
      // Previous month (for comparison)
      prevEndDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      prevStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      break;
    
    case 'year':
      // Current year (from January 1st to today)
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      
      // Previous year (for comparison)
      prevEndDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      prevStartDate = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      break;
    
    // Legacy cases - keep for backward compatibility
    // Legacy cases - keep for backward compatibility
    case 'yesterday':
      // Yesterday
      endDate = new Date(now);
      endDate.setDate(now.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
      
      startDate = new Date(endDate);
      startDate.setHours(0, 0, 0, 0);
      
      // Day before yesterday
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
      
    case 'lastWeek':
      // Last week
      endDate = new Date(now);
      endDate.setDate(now.getDate() - (now.getDay() + 1));
      endDate.setHours(23, 59, 59, 999);
      
      startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      
      // Week before last week
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevEndDate.getDate() - 6);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
      
    case 'lastMonth':
      // Last month
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      
      // Month before last month
      prevEndDate = new Date(startDate);
      prevEndDate.setDate(0);
      prevEndDate.setHours(23, 59, 59, 999);
      
      prevStartDate = new Date(prevEndDate.getFullYear(), prevEndDate.getMonth() - 1, 1, 0, 0, 0, 0);
      break;
      
    case 'lastYear':
      // Last year
      endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      startDate = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      
      // Year before last year
      prevEndDate = new Date(now.getFullYear() - 2, 11, 31, 23, 59, 59, 999);
      prevStartDate = new Date(now.getFullYear() - 2, 0, 1, 0, 0, 0, 0);
      break;
    
    case 'currentYear':
      // Current year (from January 1st to today)
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      
      // Last year (for comparison)
      prevEndDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      prevStartDate = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      break;
    
    case 'currentDay':
      // Today
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      
      // Yesterday (for comparison)
      prevEndDate = new Date(now);
      prevEndDate.setDate(now.getDate() - 1);
      prevEndDate.setHours(23, 59, 59, 999);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
    
    case 'currentWeek': {
      // Current week (from Monday to today)
      const dayOfWeekCurrent = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const daysFromMondayCurrent = dayOfWeekCurrent === 0 ? 6 : dayOfWeekCurrent - 1; // Convert Sunday to 6
      
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now);
      startDate.setDate(now.getDate() - daysFromMondayCurrent);
      startDate.setHours(0, 0, 0, 0);
      
      // Previous week (same length as current week so far)
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevEndDate.getDate() - daysFromMondayCurrent);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
    }
    
    case 'currentMonth':
      // Current month (from 1st of this month to today)
      endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      
      // Previous month (for comparison)
      prevEndDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      prevStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      break;
    
    case 'previousDay':
      // Yesterday
      endDate = new Date(now);
      endDate.setDate(now.getDate() - 1);
      endDate.setHours(23, 59, 59, 999);
      startDate = new Date(endDate);
      startDate.setHours(0, 0, 0, 0);
      
      // Day before yesterday
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
    
    case 'previousWeek':
      // Previous week (last complete week)
      const lastSunday = new Date(now);
      lastSunday.setDate(now.getDate() - (now.getDay() || 7)); // Last Sunday
      lastSunday.setHours(23, 59, 59, 999);
      
      endDate = lastSunday;
      startDate = new Date(lastSunday);
      startDate.setDate(lastSunday.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
      
      // Week before previous week
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevEndDate.getDate() - 6);
      prevStartDate.setHours(0, 0, 0, 0);
      break;
    
    case 'previousMonth':
      // Previous month (last complete month)
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      
      // Month before previous month
      prevEndDate = new Date(startDate);
      prevEndDate.setDate(0);
      prevEndDate.setHours(23, 59, 59, 999);
      prevStartDate = new Date(prevEndDate.getFullYear(), prevEndDate.getMonth() - 1, 1, 0, 0, 0, 0);
      break;
    
    case 'previousYear':
      // Previous year (last complete year)
      endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      startDate = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      
      // Year before previous year
      prevEndDate = new Date(now.getFullYear() - 2, 11, 31, 23, 59, 59, 999);
      prevStartDate = new Date(now.getFullYear() - 2, 0, 1, 0, 0, 0, 0);
      break;
    
    default:
      // Default to last 30 days
      endDate = new Date(now);
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 30);
      
      prevEndDate = new Date(startDate);
      prevEndDate.setMilliseconds(-1);
      
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevEndDate.getDate() - 30);
  }
  
  return {
    current: { start: startDate, end: endDate },
    previous: { start: prevStartDate, end: prevEndDate }
  };
};

// Action Creators
export const fetchActiveJobsCount = (count) => ({
  type: FETCH_ACTIVE_JOBS_COUNT,
  payload: count
});

export const fetchActiveClientsCount = (count) => ({
  type: FETCH_ACTIVE_CLIENTS_COUNT,
  payload: count
});

export const fetchTotalRevenue = (amount) => ({
  type: FETCH_TOTAL_REVENUE,
  payload: amount
});

export const fetchCalendarJobs = (jobs) => ({
  type: FETCH_CALENDAR_JOBS,
  payload: jobs
});

// Thunk action creators for API calls
/** Samo klijenti — poslovi se učitavaju kroz jobSlice (getJobs) da ne dupliramo teški GET /jobs. */
export const loadDashboardMetrics = () => async (dispatch, getState) => {
  try {
    const clientsResponse = await API.get('/client');
    const clients = Array.isArray(clientsResponse?.data) ? clientsResponse.data : [];
    dispatch(fetchActiveClientsCount(clients.length));

    const jobs = getState().job?.jobs;
    if (Array.isArray(jobs) && jobs.length > 0) {
      const completedJobs = jobs.filter((job) => job.status === 'Completed');
      const totalRevenue = completedJobs.reduce((sum, job) => {
        if (job.report?.serviceCharged) return sum + parseMoneyAmountSR(job.report.serviceCharged);
        if (job.servicePrice) return sum + (parseFloat(job.servicePrice) || 0);
        return sum;
      }, 0);
      dispatch(fetchActiveJobsCount(jobs.length));
      dispatch(fetchTotalRevenue(totalRevenue));
    }
  } catch (error) {
    // Silently handle errors - endpoints may not exist yet
  }
};

// Get active jobs count
export const getActiveJobsCount = (period) => async (dispatch) => {
  try {
    const dateRanges = getDateRangeForPeriod(period);
    
    // Fetch jobs for current period
    const currentResponse = await API.get('/jobs', {
      params: {
        startDate: dateRanges.current.start.toISOString(),
        endDate: dateRanges.current.end.toISOString()
      }
    });
    
    // Fetch jobs for previous period
    const previousResponse = await API.get('/jobs', {
      params: {
        startDate: dateRanges.previous.start.toISOString(),
        endDate: dateRanges.previous.end.toISOString()
      }
    });
    
    // Handle different response structures
    // Axios wraps the response, so data is in response.data
    const currentJobs = Array.isArray(currentResponse?.data) 
      ? currentResponse.data 
      : [];
    const previousJobs = Array.isArray(previousResponse?.data) 
      ? previousResponse.data 
      : [];
    
    const current = currentJobs.length;
    const previous = previousJobs.length;

    dispatch({
      type: FETCH_ACTIVE_JOBS_COUNT,
      payload: {
        current,
        previous,
        period
      }
    });
    
    return { current, previous, period };
  } catch (error) {
    dispatch({
      type: FETCH_ACTIVE_JOBS_COUNT,
      payload: {
        current: 0,
        previous: 0,
        period
      }
    });
    
    return { current: 0, previous: 0, period };
  }
};

// Get active clients count
export const getActiveClientsCount = (period) => async (dispatch) => {
  try {
    const dateRanges = getDateRangeForPeriod(period);
    
    // Fetch all clients (we'll filter by creation date on client side)
    const allClientsResponse = await API.get('/client');
    const allClients = Array.isArray(allClientsResponse?.data) ? allClientsResponse.data : [];
    
    // Filter clients by creation date
    const filterByDate = (clients, startDate, endDate) => {
      return clients.filter(client => {
        if (!client.createdAt) return false;
        const clientDate = new Date(client.createdAt);
        return clientDate >= startDate && clientDate <= endDate;
      });
    };
    
    const currentClients = filterByDate(
      allClients,
      dateRanges.current.start,
      dateRanges.current.end
    );
    
    const previousClients = filterByDate(
      allClients,
      dateRanges.previous.start,
      dateRanges.previous.end
    );
    
    const current = currentClients.length;
    const previous = previousClients.length;

    dispatch({
      type: FETCH_ACTIVE_CLIENTS_COUNT,
      payload: {
        current,
        previous,
        period
      }
    });
    
    return { current, previous, period };
  } catch (error) {
    dispatch({
      type: FETCH_ACTIVE_CLIENTS_COUNT,
      payload: {
        current: 0,
        previous: 0,
        period
      }
    });
    
    return { current: 0, previous: 0, period };
  }
};

// Get total revenue
export const getTotalRevenue = (period) => async (dispatch) => {
  try {
    const dateRanges = getDateRangeForPeriod(period);
    
    // Fetch jobs with a wide date range to ensure we get all relevant jobs
    // We'll filter by completion date on client side since API filters by serviceDate
    const now = new Date();
    const wideStartDate = new Date(now.getFullYear() - 2, 0, 1); // 2 years ago
    const wideEndDate = new Date(now.getFullYear() + 1, 11, 31); // Next year
    
    let allJobs = [];
    try {
      const allJobsResponse = await API.get('/jobs', {
        params: {
          startDate: wideStartDate.toISOString(),
          endDate: wideEndDate.toISOString()
        }
      });
      allJobs = Array.isArray(allJobsResponse?.data) ? allJobsResponse.data : [];
    } catch (apiError) {
      try {
        const fallbackResponse = await API.get('/jobs');
        allJobs = Array.isArray(fallbackResponse?.data) ? fallbackResponse.data : [];
      } catch (fallbackError) {
        allJobs = [];
      }
    }
    
    // Filter jobs by completion date (when they were actually completed)
    const filterByCompletionDate = (jobs, startDate, endDate) => {
      return jobs.filter(job => {
        // Only count completed jobs
        if (job.status !== 'Completed') return false;
        
        // Must have a report to be considered completed
        if (!job.report) return false;
        
        // Use report.completedAt as the primary completion date (when job was actually completed)
        // Fallback to submittedAt if completedAt is not available
        let completionDate = null;
        if (job.report.completedAt) {
          completionDate = new Date(job.report.completedAt);
        } else if (job.report.submittedAt) {
          completionDate = new Date(job.report.submittedAt);
        }
        
        // If no completion date in report, skip this job (don't use updatedAt as it's unreliable)
        if (!completionDate || isNaN(completionDate.getTime())) {
          return false;
        }
        
        // Normalize dates appropriately:
        // - startDate: normalize to start of day (00:00:00)
        // - endDate: normalize to end of day (23:59:59.999) to include the entire day
        // - completionDate: normalize to start of day for comparison
        const normalizeToStartOfDay = (date) => {
          const normalized = new Date(date);
          normalized.setHours(0, 0, 0, 0);
          return normalized;
        };
        
        const normalizeToEndOfDay = (date) => {
          const normalized = new Date(date);
          normalized.setHours(23, 59, 59, 999);
          return normalized;
        };
        
        // Normalize completion date to start of day for comparison
        const normalizedCompletion = normalizeToStartOfDay(completionDate);
        
        // Normalize start and end dates
        const normalizedStart = normalizeToStartOfDay(startDate);
        // endDate should include the entire day, so normalize to end of day
        const normalizedEnd = normalizeToEndOfDay(endDate);
        
        // Check if completion date is within the period
        // Since completionDate is normalized to start of day (00:00:00),
        // and endDate is normalized to end of day (23:59:59.999),
        // we need to check if completionDate is >= startDate AND <= endDate
        // But since completionDate is at 00:00:00, we need to make sure it's within the day range
        // So we check: completionDate >= startDate AND completionDate <= endDate
        // This works because 00:00:00 of a day is <= 23:59:59.999 of the same day
        const isInRange = normalizedCompletion.getTime() >= normalizedStart.getTime() && 
                         normalizedCompletion.getTime() <= normalizedEnd.getTime();
        
        // Additional check: make sure completion date is on the same calendar day as the range
        // This handles edge cases where timezone differences might cause issues
        const completionDateOnly = new Date(normalizedCompletion.getFullYear(), normalizedCompletion.getMonth(), normalizedCompletion.getDate());
        const startDateOnly = new Date(normalizedStart.getFullYear(), normalizedStart.getMonth(), normalizedStart.getDate());
        const endDateOnly = new Date(normalizedEnd.getFullYear(), normalizedEnd.getMonth(), normalizedEnd.getDate());
        
        const isSameDay = completionDateOnly.getTime() >= startDateOnly.getTime() && 
                         completionDateOnly.getTime() <= endDateOnly.getTime();
        
        const finalIsInRange = isInRange && isSameDay;
        
        return finalIsInRange;
      });
    };
    
    const currentJobs = filterByCompletionDate(
      allJobs,
      dateRanges.current.start,
      dateRanges.current.end
    );
    
    const previousJobs = filterByCompletionDate(
      allJobs,
      dateRanges.previous.start,
      dateRanges.previous.end
    );
    
    // Calculate revenue from completed jobs
    const calculateRevenue = (jobs) => {
      return jobs.reduce((sum, job) => {
        // Try to get revenue from report.serviceCharged first (most accurate)
        if (job.report && job.report.serviceCharged) {
          return sum + parseMoneyAmountSR(job.report.serviceCharged);
        }
        // Fallback to servicePrice if available
        if (job.servicePrice) {
          return sum + (parseFloat(job.servicePrice) || 0);
        }
        return sum;
      }, 0);
    };
    
    const current = calculateRevenue(currentJobs);
    const previous = calculateRevenue(previousJobs);

    dispatch({
      type: FETCH_TOTAL_REVENUE,
      payload: {
        current,
        previous,
        period
      }
    });
    
    return { current, previous, period };
  } catch (error) {
    dispatch({
      type: FETCH_TOTAL_REVENUE,
      payload: {
        current: 0,
        previous: 0,
        period
      }
    });
    
    return { current: 0, previous: 0, period };
  }
};

// Get webshop orders count
export const getWebshopOrdersCount = (period) => async (dispatch) => {
  try {
    const dateRanges = getDateRangeForPeriod(period);
    
    // Fetch orders for current period
    const currentResponse = await API.get('/webshop/orders');
    
    // Fetch orders for previous period - we need to filter by date on client side
    // since API doesn't support date filtering
    const allOrdersResponse = await API.get('/webshop/orders');
    const allOrders = Array.isArray(allOrdersResponse?.data) ? allOrdersResponse.data : [];
    
    // Filter orders by creation date
    const filterByDate = (orders, startDate, endDate) => {
      return orders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });
    };
    
    const currentOrders = filterByDate(
      allOrders,
      dateRanges.current.start,
      dateRanges.current.end
    );
    
    const previousOrders = filterByDate(
      allOrders,
      dateRanges.previous.start,
      dateRanges.previous.end
    );
    
    const current = currentOrders.length;
    const previous = previousOrders.length;

    dispatch({
      type: FETCH_WEBSHOP_ORDERS_COUNT,
      payload: {
        current,
        previous,
        period
      }
    });
    
    return { current, previous, period };
  } catch (error) {
    dispatch({
      type: FETCH_WEBSHOP_ORDERS_COUNT,
      payload: {
        current: 0,
        previous: 0,
        period
      }
    });
    
    return { current: 0, previous: 0, period };
  }
};

// Get jobs for calendar view
export const getCalendarJobs = (startDate, endDate) => async (dispatch) => {
  try {
    // Ensure we have valid dates
    if (!startDate || !endDate) {
      throw new Error('Invalid date range');
    }

    // Get jobs from the API
    const response = await API.get('/jobs', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    });

    dispatch({
      type: FETCH_CALENDAR_JOBS,
      payload: response.data
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: FETCH_CALENDAR_JOBS,
      payload: []
    });
    
    return [];
  }
}; 